import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonAvatar,
  IonLabel,
  IonNote,
  IonIcon,
  IonAlert,
  IonSpinner,
  useIonToast,
} from '@ionic/react';
import { trash, closeOutline } from 'ionicons/icons';
import { useMutation } from '@apollo/client/react';
import { useState, useCallback } from 'react';
import { REMOVE_FANTASY_LEAGUE_MEMBER } from '../../../../graphql/mutations/leagueAdmin';
import { GET_FANTASY_LEAGUE } from '../../../../graphql/queries/fantasyLeague';
import { GET_ACTIVE_MARKET_ENTRIES } from '../../../../graphql/queries/activeMarketEntries';
import { GET_VISIBLE_BIDS } from '../../../../graphql/queries/visibleBids';
import { GET_MY_MARKET_BIDS } from '../../../../graphql/queries/myMarketBids';
import { withAvatarCacheBuster } from '../../../../lib/avatarUrl';
import type {
  RemoveFantasyLeagueMemberMutation,
  RemoveFantasyLeagueMemberMutationVariables,
  GetFantasyLeagueQuery,
} from '../../../../types/graphql-generated';

type Member = NonNullable<GetFantasyLeagueQuery['fantasyLeague']>['members'][number];

interface ExpelMemberModalProps {
  isOpen: boolean;
  leagueId: string;
  members: Member[];
  onClose: () => void;
}

export const ExpelMemberModal = ({
  isOpen,
  leagueId,
  members,
  onClose,
}: ExpelMemberModalProps) => {
  const [presentToast] = useIonToast();
  const [memberToExpel, setMemberToExpel] = useState<{ id: string; name: string } | null>(null);

  const [removeMember, { loading }] = useMutation<
    RemoveFantasyLeagueMemberMutation,
    RemoveFantasyLeagueMemberMutationVariables
  >(REMOVE_FANTASY_LEAGUE_MEMBER, {
    refetchQueries: [
      { query: GET_FANTASY_LEAGUE, variables: { id: leagueId } },
      { query: GET_ACTIVE_MARKET_ENTRIES, variables: { fantasyLeagueId: leagueId } },
      { query: GET_VISIBLE_BIDS, variables: { fantasyLeagueId: leagueId } },
      { query: GET_MY_MARKET_BIDS, variables: { fantasyLeagueId: leagueId } },
    ],
    onCompleted: (res) => {
      const errors = res.removeFantasyLeagueMember?.errors ?? [];
      if (errors.length === 0) {
        presentToast({
          message: 'Participante expulsado correctamente',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-success',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
        onClose();
      } else {
        presentToast({
          message: errors[0] ?? 'Error al expulsar al participante',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-danger',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
      }
    },
    onError: (error) => {
      presentToast({
        message: `Error: ${error.message}`,
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'app-footer',
        cssClass: 'toast-danger',
        buttons: [{ icon: closeOutline, role: 'cancel' }],
      });
    },
  });

  const handleConfirmExpel = useCallback(() => {
    if (!memberToExpel) return;
    removeMember({ variables: { input: { fantasyLeagueMemberId: memberToExpel.id } } });
    setMemberToExpel(null);
  }, [removeMember, memberToExpel]);

  const regularMembers = members.filter((m) => m.role === 'member');

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Expulsar participante</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>Cerrar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        ) : regularMembers.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[200px] gap-2">
            <IonLabel color="medium" className="text-center">
              No hay participantes que puedas expulsar
            </IonLabel>
          </div>
        ) : (
          <IonList>
            {regularMembers.map((member) => (
              <IonItem key={member.id}>
                <IonAvatar slot="start" className="me-3">
                  {member.user.picture ? (
                    <img
                      src={withAvatarCacheBuster(member.user.picture, member.user.updatedAt) ?? undefined}
                      alt={`Foto de ${member.user.fullName ?? 'participante'}`}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center rounded-full">
                      <span className="text-white font-bold text-sm">
                        {(member.user.fullName ?? '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </IonAvatar>
                <IonLabel>
                  <h2 className="font-semibold">{member.user.fullName ?? 'Participante'}</h2>
                  {member.teamName ? <IonNote>{member.teamName}</IonNote> : null}
                </IonLabel>
                <IonButton
                  slot="end"
                  fill="clear"
                  color="danger"
                  aria-label={`Expulsar a ${member.user.fullName ?? 'participante'}`}
                  onClick={() =>
                    setMemberToExpel({
                      id: member.id,
                      name: member.user.fullName ?? 'este participante',
                    })
                  }
                >
                  <IonIcon icon={trash} slot="icon-only" />
                </IonButton>
              </IonItem>
            ))}
          </IonList>
        )}

        <IonAlert
          isOpen={memberToExpel !== null}
          onDidDismiss={() => setMemberToExpel(null)}
          header="Expulsar participante"
          message={`¿Seguro que quieres expulsar a ${memberToExpel?.name ?? 'este participante'}? Esta acción no se puede deshacer.`}
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Expulsar', role: 'confirm', cssClass: 'danger', handler: handleConfirmExpel },
          ]}
        />
      </IonContent>
    </IonModal>
  );
};
